/**
 * WeightPhotoUploads.tsx — ONE multi-photo upload machine for the weight photo,
 * shared by every surface that asks for one.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: photo at plan selection
 * (CTO wave 3 patient side, 2026-08-27).
 *
 * WHY A SHARED MODULE. The weight photo is asked for on several surfaces — the
 * deferred-photo prompt on the thank-you page and the dashboard (patient lane)
 * and the staff evidence card (staff lane). Each one is a different lane (guest
 * grant vs the signed-in patient's self-scoped grant vs the staff grant) but the
 * SAME promise to the patient: "this photo reached your clinician". Several
 * implementations of that promise would drift, and the drift would be invisible
 * — a surface that says "sent" while the PUT failed tells a patient their
 * clinician has evidence nobody has. So the grant → signed PUT → per-file status
 * machine lives here once.
 *
 * ⚠️ NO SURFACE GATES ON A PHOTO ANY MORE (2026-09-04). The plan step asked for
 * one and held "Continue to delivery" shut until it landed; that is removed —
 * see the header of `checkout/PlanSelection.tsx` for the decision and the funnel
 * measurement behind it. The `exhausted` flag this module used to publish went
 * with it, and the reason it existed is worth keeping in view: on the mandatory
 * lane, a closed upload lane with nothing on file rendered a screen with NO WAY
 * OFF IT — no picker, no retry, no defer link and a dead CTA, i.e. a patient
 * permanently unable to pay. `exhausted` was "nothing this surface can still do
 * will produce a photo", and the gate had to open on it. ⚠️ IF A GATE IS EVER
 * PROPOSED HERE AGAIN, that flag (`storedCount === 0 && !busy && !canPick &&
 * !canRetry`) has to come back with it — but read the standing rule in CLAUDE.md
 * first, because the answer is meant to be no.
 *
 * ⚠️ R41 — THE SERVER IS THE CONTROL, THIS IS AN AFFORDANCE. {@link
 * MAX_WEIGHT_PHOTOS} mirrors clinic-service's `MAX_PHOTOS_PER_ASSESSMENT` so the
 * UI can stop offering a slot it knows is gone, but it decides nothing: the
 * grant lanes re-count under a `FOR UPDATE` row lock and 409 past the cap, and
 * they 409 again once the linked order is dispatched. A 409 is therefore treated
 * as the truth and the picker closes — never the other way round.
 *
 * ⚠️ REMOVE ONLY REMOVES WHAT WAS NEVER STORED, AND THAT IS NOT A SHORTCUT.
 * A "Remove" on a photo that has already been PUT would take the thumbnail off
 * this screen while the clinician still has the image — the patient would
 * believe they had withdrawn something they had not. So the control is offered
 * on a FAILED tile (nothing reached the bucket, so removing it is true) and
 * never on a stored one.
 *
 * ⚠️ CORRECTED 2026-09-06 — A SERVER RETRACT LANE NOW EXISTS, AND THIS RULE IS
 * UNCHANGED BY IT. This block used to close with "there is no delete lane:
 * clinic-service appends an `AssessmentPhoto` row at GRANT time and offers no
 * way to retract one… giving stored photos a real Remove means a server retract
 * lane first". The lane shipped that day —
 * `DELETE /v1/clinic/assessments/:id/photos/:photoId` — but it is
 * `@Roles('ADMIN', 'CLINICIAN')` and a PATIENT is refused deliberately: the
 * evidence behind a POM supply that has already happened must outlive their
 * second thoughts, so their route is a DPO erasure request with an Article 9
 * balancing test (UK GDPR Art. 17(3)(b)/(h)), not a button. This module is the
 * PATIENT-and-staff-shared uploader, so the existence of the lane is NOT the
 * missing precondition it was written as — do not read the old sentence as a
 * standing invitation to wire this Remove up to it.
 *
 * ⚠️ THE SLOT IS SPENT AT GRANT TIME, NOT AT PUT TIME — AND MISCOUNTING THAT
 * WAS A DEAD END (fixed 2026-08-27). `recordPhotoGrant` inserts the
 * `AssessmentPhoto` row BEFORE the browser has uploaded anything, so a grant
 * that succeeded and a PUT that then failed has still consumed one of the five.
 * This machine used to count only what it had STORED, so five failed PUTs read
 * as "five slots free" here and "cap reached" on the server: the sixth pick
 * 409'd, the lane closed, the picker and the retry both went, and a patient on
 * the mandatory lane was left with a disabled Continue and no way to change it —
 * permanently unable to pay. So a tile whose GRANT succeeded now HOLDS its slot
 * for as long as it exists ({@link holdsSlot}), and a retry re-PUTs the grant
 * the tile is already carrying instead of minting a second one. Re-granting is
 * reserved for the one case where the held grant genuinely cannot work again:
 * the signed URL has expired.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import type { PhotoUploadGrant } from '../api/types';
import { grantStaffPhotoUpload } from '../api/clinic';
import { grantMyWeightPhotoUpload, grantPhotoUpload, uploadPhoto } from '../api/journey';
import { Button, Help } from '../ui/kit';

/**
 * The per-assessment photo cap, mirroring clinic-service's
 * `MAX_PHOTOS_PER_ASSESSMENT` (photo-storage.ts). Five angles of a scale is
 * evidence; fifty is a storage-abuse primitive, which is why the server caps it
 * at all. Mirrored here ONLY so the picker can stop inviting a photo it knows
 * will be refused — see the R41 note in the file header.
 */
export const MAX_WEIGHT_PHOTOS = 5;

/**
 * WHICH grant lane this surface may use. They are not interchangeable:
 *
 *  - `guest` posts `:id/photo-upload?clinicSlug=`, which the gateway stamps with
 *    an ANONYMOUS patient principal. clinic-service then binds the write to the
 *    slug and refuses it outright once `Assessment.patientUserId` is set to
 *    somebody else — so it is the lane for a patient who has not signed in.
 *  - `patient` posts the authed, id-less `mine/weight-photo`, where the server
 *    self-scopes to the caller's OWN latest submitted assessment (R41: the id is
 *    never an input, so it cannot be used to enumerate anyone else's).
 *
 *  - `staff` posts the authed `:id/photo-upload/staff` (clinic #166,
 *    2026-09-04). A DISTINCT path from the guest lane so the gateway does not
 *    anonymise it: the verified CLINICIAN/ADMIN role is the bind — no
 *    `clinicSlug` — and it is what lets the ledger record that a member of
 *    STAFF obtained this evidence rather than the patient supplying it
 *    (`addedByRole`). Same grant, same ledger, same five-photo cap, same
 *    `FOR UPDATE` serialisation: not a second upload path.
 *
 * Plan selection is always POST-SUBMIT (it sits after the result screen), which
 * is what makes the patient lane usable there at all — it only ever answers for
 * a submitted assessment.
 */
export type WeightPhotoLane =
  | { kind: 'guest'; assessmentId: string; clinicSlug: string }
  | { kind: 'patient' }
  | { kind: 'staff'; assessmentId: string };

/** `sending` → the PUT is in flight; `stored` → the bucket has it; `failed` → nothing was stored. */
export type WeightPhotoTileStatus = 'sending' | 'stored' | 'failed';

/**
 * WHICH HALF OF THE TWO-STEP UPLOAD A `failed` TILE FELL AT, and it is the
 * difference between a slot spent and a slot free (see the file header):
 *
 *  - `grant` — the server never issued one. No `AssessmentPhoto` row exists, so
 *    nothing was consumed and a retry has to ask again from scratch.
 *  - `upload` — the grant succeeded and the signed PUT did not. The row IS
 *    there, the slot IS gone, and the grant this tile holds is the only way to
 *    fill it: retrying re-PUTs it rather than burning a second slot.
 */
export type WeightPhotoFailurePhase = 'grant' | 'upload' | 'too_large';

/**
 * The size the SIGNATURE allows, mirrored from clinic-service's
 * `MAX_PHOTO_BYTES` (photo-storage.ts).
 *
 * ⚠️ IT IS PINNED INTO THE V4 SIGNATURE, so GCS rejects an over-size PUT at the
 * URL itself — the client cannot talk it round. Checking here is therefore not
 * defence in depth, it is the only way to tell the patient WHY before a slot is
 * spent on a file that can never land.
 *
 * If the server's constant moves, this one has to move with it. It is a
 * duplicated literal on purpose: the alternative is echoing the limit on the
 * grant, and the grant is minted AFTER the point where this check has to happen.
 */
export const MAX_WEIGHT_PHOTO_BYTES = 10 * 1024 * 1024;

export interface WeightPhotoTile {
  /**
   * A local key, and DELIBERATELY not the file name. R19: a file name is
   * patient-supplied free text that can carry a name or a date of birth, and it
   * would end up in the DOM (and so in any analytics that scrapes it) for no
   * benefit — the tiles are numbered in the UI instead.
   */
  id: string;
  status: WeightPhotoTileStatus;
  /** An object URL for the local preview, or null where the browser has none (jsdom). */
  previewUrl: string | null;
  /** Set only on a `failed` tile — see {@link WeightPhotoFailurePhase}. */
  failedAt?: WeightPhotoFailurePhase;
}

export interface WeightPhotoUploads {
  tiles: WeightPhotoTile[];
  /** Photos this surface has CONFIRMED are in the bucket. The gate reads this. */
  storedCount: number;
  /**
   * Slots the UI believes are still free: the cap minus every tile that HOLDS a
   * server slot — stored, in-flight, or grant-succeeded-PUT-failed. A failed PUT
   * is a spent slot (the header explains why), so it is counted here even though
   * the clinician has nothing from it.
   */
  remaining: number;
  /** The server has refused a grant (cap hit, or the order is already dispatched). */
  laneClosed: boolean;
  /** True while any tile is mid-PUT — the CTA waits rather than racing it. */
  busy: boolean;
  /** More files were picked than there were slots; the extras were dropped. */
  overflowed: boolean;
  addFiles: (files: FileList | File[] | null) => void;
  retry: (id: string) => void;
  remove: (id: string) => void;
}

/**
 * Does this tile hold one of the server's five slots? Everything except a tile
 * whose GRANT failed — see the file header: the `AssessmentPhoto` row is
 * appended at grant time, so a failed PUT has still spent one.
 */
function holdsSlot(tile: WeightPhotoTile): boolean {
  // `too_large` never reached the server, so like a failed grant it holds
  // nothing — the patient can pick another photo in its place.
  return tile.status !== 'failed' || tile.failedAt === 'upload';
}

/**
 * Is the signed URL this tile is holding past its expiry?
 *
 * ⚠️ ABSENT `expiresAt` MEANS "REUSE IT", NOT "RE-GRANT". The only reason to
 * mint a second grant for a tile is that the first one demonstrably cannot work
 * again — and minting one costs a slot the patient may need. A grant that does
 * not state an expiry (a stub grant, or an older server) gives us no evidence of
 * expiry, so the cheap, non-destructive move is to re-PUT it: if it really has
 * expired, GCS 403s and the tile fails exactly as it does now, having spent
 * nothing extra.
 */
function grantExpired(grant: PhotoUploadGrant): boolean {
  if (!grant.expiresAt) return false;
  const at = Date.parse(grant.expiresAt);
  return Number.isFinite(at) && at <= Date.now();
}

/** An object URL when the browser can make one; null in jsdom (no implementation). */
function previewUrlFor(file: File): string | null {
  try {
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;
    return URL.createObjectURL(file);
  } catch {
    return null;
  }
}

function revokePreview(url: string | null): void {
  if (url === null) return;
  try {
    if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
  } catch {
    /* Nothing to release in an environment that never minted one. */
  }
}

/**
 * The upload machine. `lane` of `null` disables it entirely (the standalone plan
 * chooser has no assessment to attach a photo to) — passing null rather than
 * skipping the hook keeps the call unconditional, which is what React requires.
 *
 * The picked `File` objects are held in a ref, not in state: they are only ever
 * needed by the retry, they are large, and putting them in state would re-render
 * every tile each time one byte-buffer arrived.
 */
export function useWeightPhotoUploads(lane: WeightPhotoLane | null): WeightPhotoUploads {
  const [tiles, setTiles] = useState<WeightPhotoTile[]>([]);
  const [laneClosed, setLaneClosed] = useState(false);
  const [overflowed, setOverflowed] = useState(false);
  const pending = useRef(new Map<string, File>());
  /**
   * The grant each tile is holding, kept until its PUT succeeds. This is what
   * makes a retry re-PUT rather than re-grant (see the file header): the slot is
   * already spent, so asking for a second grant would spend a second one and
   * march the patient towards the cap on nothing but bad luck with the network.
   * A ref, not state, for the same reason the files are: nothing renders from it.
   */
  const grants = useRef(new Map<string, PhotoUploadGrant>());
  const seq = useRef(0);
  /* Uploads outlive a fast unmount (a patient who presses Continue mid-PUT), so
     every setState below is guarded — otherwise React warns, and worse, the
     warning trains the next reader to ignore real ones. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * THE AUTHORITATIVE MIRROR OF `tiles`, and the reason every write goes through
   * `applyTiles` rather than a bare `setTiles`.
   *
   * The cap arithmetic has to read the CURRENT tiles, and a functional updater is
   * the wrong place to do it: an updater must stay pure, React invokes it twice
   * under StrictMode, and this one needs to start network requests. Reading state
   * inside the updater and firing the PUTs from there uploaded every photo twice
   * in development. So the ref is updated synchronously alongside the state, the
   * arithmetic reads the ref, and the side effects happen in plain code after.
   */
  const tilesRef = useRef<WeightPhotoTile[]>([]);
  const applyTiles = useCallback((update: (current: WeightPhotoTile[]) => WeightPhotoTile[]) => {
    const next = update(tilesRef.current);
    tilesRef.current = next;
    if (alive.current) setTiles(next);
  }, []);

  /* Release every object URL on unmount. Without this each picked photo leaks a
     blob for the lifetime of the tab, and a patient re-picking a 10 MB photo a
     few times is exactly who this screen is built for. */
  useEffect(
    () => () => {
      for (const tile of tilesRef.current) revokePreview(tile.previewUrl);
    },
    [],
  );

  const setStatus = useCallback(
    (id: string, status: WeightPhotoTileStatus) => {
      /* `failedAt` is cleared on every non-failed transition so a tile that fails
         at the grant, is retried, and then fails at the PUT cannot keep the
         stale phase — the slot arithmetic reads that field. */
      applyTiles((current) =>
        current.map((tile) => (tile.id === id ? { ...tile, status, failedAt: undefined } : tile)),
      );
    },
    [applyTiles],
  );

  const setFailed = useCallback(
    (id: string, failedAt: WeightPhotoFailurePhase) => {
      applyTiles((current) =>
        current.map((tile) => (tile.id === id ? { ...tile, status: 'failed' as const, failedAt } : tile)),
      );
    },
    [applyTiles],
  );

  const send = useCallback(
    async (id: string) => {
      const file = pending.current.get(id);
      if (!file || !lane) return;
      setStatus(id, 'sending');

      /* STEP 1 — THE GRANT, AND ONLY IF THIS TILE IS NOT ALREADY HOLDING A
         USABLE ONE. Re-granting a tile that already spent a slot is what
         marched patients into the cap; see the file header. */
      const held = grants.current.get(id);
      let grant = held && !grantExpired(held) ? held : undefined;
      if (!grant) {
        try {
          grant =
            lane.kind === 'guest'
              ? await grantPhotoUpload(lane.assessmentId, lane.clinicSlug)
              : lane.kind === 'staff'
                ? await grantStaffPhotoUpload(lane.assessmentId)
                : await grantMyWeightPhotoUpload();
          grants.current.set(id, grant);
        } catch (error) {
          /* THE SERVER HAS REFUSED, AND THE QUESTION IS ONLY WHETHER RETRYING
             COULD EVER HELP.
             
             409 is the documented close — the cap is full, or the order has
             shipped and new evidence can no longer change what was dispensed.
             
             ⚠️ BUT IT WAS THE *ONLY* STATUS THAT CLOSED THE LANE, AND THAT
             BECAME DANGEROUS ON 2026-08-30, when the weight photo was briefly a
             hard gate on the money path. A 404 — which `assertGuestWriteBinding`
             throws for a purged row, a clinic-slug mismatch, or an assessment
             already bound to an identity — left `laneClosed` false. So the
             picker stayed and the retry stayed: a patient in an infinite retry
             loop against a server that will refuse every time, on a screen they
             could not leave, because the CTA was waiting on a photo that could
             never arrive.
             
             THE GATE IS GONE (2026-09-04) AND THIS RULE STAYS, because it was
             never really about the gate. A 4xx means the server is refusing THIS
             request and will refuse the identical retry, so offering that retry
             is a lie about what pressing it can do. A 5xx or a transport fault
             is transient and keeps its retry, and 429 is explicitly transient
             too.
             
             R41 is untouched either way: orders-service still refuses the
             prescription with no evidence behind it, so a closed lane defers the
             ask to a clinician rather than skipping it. */
          const permanent =
            error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 429;
          if (permanent && alive.current) setLaneClosed(true);
          setFailed(id, 'grant');
          return;
        }
      }

      /* STEP 2 — THE SIGNED PUT. From here the slot is spent whatever happens,
         which is why the failure is tagged `upload`: the tile keeps its grant,
         keeps its place against the cap, and the retry resumes at this step. */
      try {
        await uploadPhoto(grant, file);
        /* The bytes and the grant are only needed for a retry, and there will
           not be one. */
        pending.current.delete(id);
        grants.current.delete(id);
        setStatus(id, 'stored');
      } catch {
        setFailed(id, 'upload');
      }
    },
    [lane, setFailed, setStatus],
  );

  const addFiles = useCallback(
    (picked: FileList | File[] | null) => {
      if (!lane || !picked) return;
      const chosen = Array.from(picked);
      if (chosen.length === 0) return;
      /* Only a tile whose GRANT failed leaves its slot free — everything else
         has an `AssessmentPhoto` row behind it, stored or not (see holdsSlot and
         the file header). Counting a failed PUT as a free slot is what let the
         portal invite a sixth photo the server had already refused. */
      const held = tilesRef.current.filter(holdsSlot).length;
      const accepted = chosen.slice(0, Math.max(0, MAX_WEIGHT_PHOTOS - held));
      setOverflowed(accepted.length < chosen.length);
      if (accepted.length === 0) return;
      /* ⚠️ SIZE IS CHECKED BEFORE A GRANT IS MINTED (2026-08-30), and the order
         is the whole point.
         
         The 10 MB ceiling is pinned into the V4 signature, so GCS 400s an
         over-size PUT. Left to run, that spends a slot (the AssessmentPhoto row
         is written at grant time), fails at the `upload` phase — which
         `remove()` deliberately refuses to delete, because normally the grant is
         the only way to fill that slot — and offers a "Try again" that re-PUTs
         the same too-big bytes forever. One of the five slots is gone for good,
         and the control on screen cannot recover it.
         
         In other words: a patient who picked a 12 MB photo burned a slot and saw
         "Didn't send" — which reads as our fault — and did it again on every
         retry. Modern phones at max resolution reach 10 MB easily, so this is
         the common case on a phone camera, not an edge one.
         
         Failing at `too_large` instead keeps the slot free and the tile
         removable, and the block can say what is actually wrong. */
      const added = accepted.map((file) => {
        seq.current += 1;
        const id = `wp-${seq.current}`;
        if (file.size > MAX_WEIGHT_PHOTO_BYTES) {
          return { id, status: 'failed' as const, failedAt: 'too_large' as const, previewUrl: previewUrlFor(file) };
        }
        pending.current.set(id, file);
        return { id, status: 'sending' as const, previewUrl: previewUrlFor(file) };
      });
      applyTiles((current) => [...current, ...added]);
      for (const tile of added) if (tile.status === 'sending') void send(tile.id);
    },
    [applyTiles, lane, send],
  );

  const retry = useCallback(
    (id: string) => {
      if (laneClosed) return;
      void send(id);
    },
    [laneClosed, send],
  );

  const remove = useCallback(
    (id: string) => {
      const tile = tilesRef.current.find((t) => t.id === id);
      /* See the header: a STORED photo cannot be taken back from here, so the
         control is never offered for one — and this guard means a stray call
         cannot quietly fake a retraction either.
         A tile that failed at the PUT is refused for a different reason: its
         slot is already spent server-side, so dropping the tile would hide a
         consumed slot AND throw away the grant that is the only way to fill it.
         Its retry is the patient's handle on that slot; taking the tile away
         would leave them with neither. */
      // `too_large` is removable: no grant was minted, so no slot was spent.
      if (!tile || tile.status === 'stored' || tile.failedAt === 'upload') return;
      revokePreview(tile.previewUrl);
      pending.current.delete(id);
      grants.current.delete(id);
      applyTiles((current) => current.filter((t) => t.id !== id));
    },
    [applyTiles],
  );

  const storedCount = tiles.filter((tile) => tile.status === 'stored').length;
  const remaining = Math.max(0, MAX_WEIGHT_PHOTOS - tiles.filter(holdsSlot).length);
  const busy = tiles.some((tile) => tile.status === 'sending');

  return {
    tiles,
    storedCount,
    remaining,
    laneClosed,
    busy,
    overflowed,
    addFiles,
    retry,
    remove,
  };
}

/**
 * The "choose photos" control.
 *
 * THE INPUT IS VISUALLY HIDDEN AND THE BUTTON IS THE OPERABLE CONTROL. A file
 * input styled to look like a button cannot carry a visible focus ring once it
 * is off-screen, so a keyboard user would tab to something they cannot see
 * (WCAG 2.4.7). The real `<button>` here takes the focus ring and the 44px
 * target from the kit and forwards the click; the input keeps an `aria-label` so
 * it is still a labelled control in the tree, and `tabIndex={-1}` so there is
 * exactly ONE tab stop for one action.
 */
export function WeightPhotoPicker({
  id,
  label,
  busyLabel,
  busy,
  disabled,
  variant = 'primary',
  icon,
  style,
  onFiles,
}: {
  id: string;
  label: string;
  busyLabel: string;
  busy: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'ghost';
  /**
   * An optional DECORATIVE glyph beside the text (2026-09-05). The staff
   * evidence lane passes one so its two controls are told apart at a glance;
   * the patient surfaces pass none. It is never the label — `label` is still
   * the string that names this control in the tree and on the button, so the
   * accessible name is identical with the icon and without it.
   */
  icon?: ReactNode;
  /**
   * Overrides merged over the kit's `buttonStyle()`. The one that matters is
   * `width`: the kit default is `100%`, which is right on the patient's
   * stacked cards but makes this picker claim a whole flex line to itself when
   * a staff surface puts it beside another control. Absent = unchanged, so the
   * patient lanes keep their full-width target.
   */
  style?: CSSProperties;
  onFiles: (files: FileList | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        id={id}
        data-testid={id}
        type="file"
        accept="image/*"
        multiple
        aria-label={label}
        tabIndex={-1}
        className="gps-sr-only"
        onChange={(event) => {
          onFiles(event.target.files);
          /* Clear the input so re-picking the SAME file fires `change` again —
             otherwise a patient whose first attempt failed cannot re-select it. */
          event.target.value = '';
        }}
      />
      <Button
        type="button"
        variant={variant}
        disabled={disabled === true || busy}
        onClick={() => inputRef.current?.click()}
        style={style}
      >
        {/* The glyph is dropped while busy: "Adding…" is a transient state and
            a shape that stays put under changing words reads as a stuck
            control. */}
        {icon && !busy ? icon : null}
        {busy ? busyLabel : label}
      </Button>
    </>
  );
}

/**
 * The picked photos, one tile each.
 *
 * ⚠️ NEVER COLOUR ALONE (WCAG 1.4.1). Every state says what it is in words —
 * "Sending…", "Sent to your clinician", "Didn't send" — and the failed tile
 * additionally carries its own controls. The tint and the border are decoration
 * on top of a sentence, so the tiles read identically in greyscale.
 *
 * The list is `aria-live="polite"`: a photo finishing its upload is exactly the
 * kind of quiet state change a screen-reader user would otherwise have to go
 * hunting for, and it is the change that decides whether Continue is available.
 */
export function WeightPhotoTiles({
  uploads,
  retryLabel = 'Try again',
  voice = 'patient',
  storedShownElsewhere = false,
}: {
  uploads: WeightPhotoUploads;
  retryLabel?: string;
  /**
   * THE SURFACE ALREADY HAS ITS OWN AUTHORITATIVE VIEW OF WHAT IS ON FILE, so a
   * `stored` tile here would be the SAME PHOTO A SECOND TIME (2026-09-05, CTO:
   * after an admin adds evidence "the drawer shows the photo twice").
   *
   * The staff evidence card sits directly under the gallery grid, and a stored
   * upload lands in that grid the moment the re-read returns — so the drawer
   * showed one photo in the strip ("2 photos on file") and again as a tile
   * ("Added to the patient's file"). Two views of one image, disagreeing
   * whenever the re-read lagged, on the card a clinician attests against.
   *
   * ⚠️ IT HIDES THE STORED TILES ONLY, NEVER THE UNSETTLED ONES, and that is
   * the whole point of it being a filter rather than "don't render the list".
   * A tile that is SENDING or that FAILED is on no gallery anywhere — the
   * bucket has nothing to show — so dropping the list wholesale would take the
   * per-file progress, the failure sentence and the retry off the screen with
   * it, and a staff member whose upload died would see a card that simply never
   * changed. Those tiles stay; only the duplicated ones go.
   *
   * ⚠️ AND THE NUMBERING STAYS THE PICKED ORDER. "Photo 3" is the third file
   * they chose, hidden neighbours included — renumbering the survivors would
   * make the retry's accessible name point at a different photo than the one
   * the person picked.
   */
  storedShownElsewhere?: boolean;
  /**
   * WHOSE SCREEN THIS IS. Every string in this module was written for the
   * PATIENT ("Sent to your clinician", "your clinician has everything they
   * need"), and a clinician adding evidence to somebody else's file reading
   * "sent to your clinician" is not a small wrongness — it is the screen
   * describing the wrong person's act, on the one card whose whole job is
   * saying what kind of evidence this is.
   *
   * ⚠️ IT IS A VOICE SWITCH, NOT A SECOND COPY MODULE. The alternative was a
   * staff-side clone of these components, and the reason this file exists at
   * all is that three copies of "this photo reached your clinician" drift
   * invisibly (see the header). One machine, one set of states, two voices.
   */
  voice?: 'patient' | 'staff';
}) {
  /* Positions are captured BEFORE the filter, so a tile keeps the number of the
     file the person picked (see `storedShownElsewhere`). */
  const shown = uploads.tiles
    .map((tile, index) => ({ tile, position: index + 1 }))
    .filter(({ tile }) => !(storedShownElsewhere && tile.status === 'stored'));
  if (shown.length === 0) return null;
  return (
    <ul
      aria-live="polite"
      /* The group's name is what a screen-reader user hears before its rows, so
         it has to name what the rows ACTUALLY are.

         ⚠️ "Photos still uploading" was wrong for two of the three states that
         survive the filter (2026-09-05 review, defect 3). Once
         `storedShownElsewhere` is on, the tiles left are `sending`,
         `failed:upload` ("Didn't send", with a retry) and `failed:too_large`
         ("Too large — max 10 MB") — and the two that most need announcing are
         precisely the ones that have STOPPED. A member of staff hearing "photos
         still uploading" over a list of dead uploads is being told to wait for
         something that will never finish. */
      aria-label={
        storedShownElsewhere ? 'Photos still sending or that did not send' : 'Photos you have added'
      }
      style={{
        listStyle: 'none',
        margin: 'var(--gps-s-3) 0 0',
        padding: 0,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--gps-s-2)',
      }}
    >
      {shown.map(({ tile, position }) => {
        return (
          <li
            key={tile.id}
            style={{
              width: '9.5rem',
              border: '1px solid var(--gps-border)',
              borderRadius: 'var(--gps-r-lg)',
              padding: '8px',
              background: 'var(--gps-surface)',
            }}
          >
            <div
              style={{
                height: '5.5rem',
                borderRadius: 'var(--gps-r-md)',
                overflow: 'hidden',
                background: 'var(--gps-surface-2)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {tile.previewUrl ? (
                <img
                  src={tile.previewUrl}
                  /* Decorative: the patient took this photo seconds ago and the
                     status line below names it. Describing its CONTENT would mean
                     asserting what is in a clinical image we have not looked at. */
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>Photo {position}</span>
              )}
            </div>
            <p style={{ margin: '8px 0 0', fontSize: '12.5px', color: 'var(--gps-text)', lineHeight: 1.35 }}>
              {tile.status === 'sending' ? 'Sending…' : null}
              {tile.status === 'stored'
                ? voice === 'staff'
                  ? 'Added to the patient’s file'
                  : 'Sent to your clinician'
                : null}
              {/* A SIZE FAILURE SAYS SO. "Didn't send" was the only failure
                  string, and it is the one message a patient cannot act on — it
                  reads as our fault and invites a retry that re-sends the same
                  too-big file. Naming the limit turns a dead end into a task. */}
              {tile.status === 'failed' && tile.failedAt === 'too_large'
                ? 'Too large — max 10 MB. Try a smaller photo.'
                : null}
              {tile.status === 'failed' && tile.failedAt !== 'too_large' ? 'Didn’t send' : null}
            </p>
            {tile.status === 'sending' ? (
              /* INDETERMINATE ON PURPOSE. `uploadPhoto` is the shared fetch-based
                 signed PUT and fetch reports no upload progress — a percentage
                 here would be invented. A determinate bar means moving that one
                 shared helper to XHR, which is a change to every photo lane in
                 the portal, not a tweak to this tile. */
              <div
                role="progressbar"
                aria-label={`Sending photo ${position}`}
                style={{
                  marginTop: '6px',
                  height: '4px',
                  borderRadius: '2px',
                  background: 'var(--gps-border)',
                  overflow: 'hidden',
                }}
              >
                <div className="gps-photo-progress" style={{ height: '100%', width: '40%', background: 'var(--gps-btn-primary)' }} />
              </div>
            ) : null}
            {tile.status === 'failed' ? (
              <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                {/* TA-06 — the retry is a real control on the failed item, not a
                    "start over". It vanishes once the SERVER has closed the lane,
                    because retrying a 409 can only fail again — and it is absent
                    on a TOO-LARGE tile for the same reason: re-sending the same
                    oversized bytes cannot succeed, so offering it would be an
                    invitation to loop. Remove-and-pick-smaller is the way out,
                    and that control is beside it. */}
                {uploads.laneClosed || tile.failedAt === 'too_large' ? null : (
                  <button
                    type="button"
                    onClick={() => uploads.retry(tile.id)}
                    style={tileActionStyle}
                    aria-label={`${retryLabel} sending photo ${position}`}
                  >
                    {retryLabel}
                  </button>
                )}
                {/* REMOVE IS ABSENT ONCE THE SLOT IS SPENT. A tile that failed
                    at the PUT already has its `AssessmentPhoto` row on the
                    server, so removing it would free nothing and would discard
                    the grant that is the only way to fill the slot — the retry
                    beside it is the whole point of keeping the tile.
                    
                    A TOO-LARGE tile is the opposite case: no grant was ever
                    minted, so nothing is spent and removing it genuinely frees
                    the slot. `remove()` allows it, and this is the control that
                    makes the dead end a task. */}
                {tile.failedAt === 'upload' ? null : (
                  <button
                    type="button"
                    onClick={() => uploads.remove(tile.id)}
                    style={tileActionStyle}
                    aria-label={`Remove photo ${position}`}
                  >
                    Remove
                  </button>
                )}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** 44px-tall text action inside a tile (WCAG 2.5.5 target size). */
const tileActionStyle = {
  background: 'none',
  border: 0,
  padding: '11px 4px',
  minHeight: '44px',
  fontFamily: 'inherit',
  fontSize: '12.5px',
  fontWeight: 600,
  color: 'var(--gps-green-text)',
  textDecoration: 'underline',
  cursor: 'pointer',
} as const;

/**
 * The one place the "what's on file, what's left" sentence is written, so the
 * plan step and the deferred-photo prompt cannot describe the same cap
 * differently. Counts what THIS surface has confirmed stored — never a guess.
 */
export function WeightPhotoCapLine({
  uploads,
  voice = 'patient',
}: {
  uploads: WeightPhotoUploads;
  /** See {@link WeightPhotoTiles}'s `voice` — same switch, same reason. */
  voice?: 'patient' | 'staff';
}) {
  /**
   * THE STAFF VOICE IS DELIBERATELY SHORTER. The patient branches below exist to
   * keep somebody who cannot get past a mandatory gate from being stranded —
   * they name the control that can still help and admit what was dropped. A
   * clinician adding evidence is gated on nothing: they need the count, the
   * ceiling, and to be told when the server has closed the lane. Restating the
   * patient's four recovery sentences here would be copy nobody reads.
   */
  if (voice === 'staff') {
    if (uploads.laneClosed) {
      return (
        <Help style={{ marginBottom: 0 }}>
          No more photos can be added to this assessment — there is a limit of {MAX_WEIGHT_PHOTOS}, and the
          lane closes once the order has been dispatched.
        </Help>
      );
    }
    return (
      <Help style={{ marginBottom: 0 }}>
        {uploads.storedCount === 0
          ? `You can add up to ${MAX_WEIGHT_PHOTOS} photos to this patient’s file.`
          : `${uploads.storedCount} of ${MAX_WEIGHT_PHOTOS} added in this session.`}
        {uploads.overflowed ? ' Some of the files picked were dropped — there was not room for them all.' : ''}
      </Help>
    );
  }
  if (uploads.laneClosed) {
    return (
      <Help style={{ marginBottom: 0 }}>
        We can’t add more photos to this order — there’s a limit of {MAX_WEIGHT_PHOTOS}, and photos close
        once your order is on its way.
      </Help>
    );
  }
  /* THE OVERFLOW NOTICE OUTLIVES THE CAP BRANCH, and that is the whole point of
     it: the patient most likely to have picked more than five is the one who
     just filled all five, and telling them "that's all five" without telling
     them the rest were dropped leaves them believing we took photos we did not. */
  const overflow = uploads.overflowed ? ' We kept the first few — there wasn’t room for the rest.' : '';
  if (uploads.remaining === 0) {
    /* ⚠️ "NO SLOTS LEFT" IS NOT "FIVE PHOTOS SENT", and saying so was a lie
       waiting to happen: a slot is spent at GRANT time, so five spent slots can
       mean five failed uploads and nothing at all in front of the clinician.
       The sentence is therefore keyed on what is STORED, and the two lower
       branches point the patient at the only control that can still help them. */
    if (uploads.storedCount >= MAX_WEIGHT_PHOTOS) {
      return (
        <Help style={{ marginBottom: 0 }}>
          That’s all {MAX_WEIGHT_PHOTOS} photos — your clinician has everything they need.
          {overflow}
        </Help>
      );
    }
    if (uploads.busy) {
      return (
        <Help style={{ marginBottom: 0 }}>
          That’s all {MAX_WEIGHT_PHOTOS} for this order — we’re still sending them.
          {overflow}
        </Help>
      );
    }
    return (
      <Help style={{ marginBottom: 0 }}>
        {uploads.storedCount === 0
          ? 'None of your photos reached your clinician, and this order has no room to start a new one.'
          : `${uploads.storedCount} of ${MAX_WEIGHT_PHOTOS} reached your clinician, and this order has no room to start a new one.`}
        {/* The lane is still OPEN here — a closed one returned above — so the
            retry on each failed tile is real, and pointing at it is the useful
            thing to say rather than a dead end. */}
        {' Use “Try again” on a photo above.'}
        {overflow}
      </Help>
    );
  }
  return (
    <Help style={{ marginBottom: 0 }}>
      {uploads.storedCount === 0
        ? `You can add up to ${MAX_WEIGHT_PHOTOS} photos.`
        : `${uploads.storedCount} of ${MAX_WEIGHT_PHOTOS} on file — add another angle if it helps.`}
      {overflow}
    </Help>
  );
}
